import { useState } from 'react';
import { 
    LineChart, 
    Line, 
    XAxis, 
    YAxis, 
    CartesianGrid, 
    Tooltip, 
    ResponsiveContainer,
    ReferenceDot
} from 'recharts';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { cn } from '../lib/utils';

const CustomTooltip = ({ active, payload, label, unit }) => {
    if (active && payload && payload.length) {
        const data = payload[0].payload;
        return (
            <div className="bg-popover border border-primary rounded-lg p-3 shadow-lg">
                <p className="text-sm text-muted-foreground mb-1">{label}</p>
                <p className="text-lg font-mono font-bold text-primary">
                    {unit === '$' ? `$${payload[0].value.toLocaleString()}` : 
                     unit === '%' ? `${payload[0].value.toFixed(1)}%` :
                     payload[0].value.toLocaleString()}
                </p>
                {data.is_anomaly && (
                    <p className="text-xs text-destructive mt-1 font-medium">Anomaly Detected</p>
                )}
            </div>
        );
    }
    return null;
};

export const TrendChart = ({ data, kpiName, unit, loading, onRangeChange }) => {
    const [range, setRange] = useState(30);

    const handleRangeChange = (newRange) => {
        setRange(newRange);
        if (onRangeChange) {
            onRangeChange(newRange);
        }
    };

    const anomalyPoints = data?.filter(d => d.is_anomaly) || [];

    if (loading) {
        return (
            <Card className="bg-card border-border">
                <CardHeader>
                    <Skeleton className="h-6 w-48" />
                </CardHeader>
                <CardContent>
                    <Skeleton className="h-[300px] w-full" />
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className="bg-card border-border" data-testid={`trend-chart-${kpiName?.toLowerCase().replace(/\s+/g, '-')}`}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-lg font-semibold text-card-foreground">
                    {kpiName} Trend
                </CardTitle>
                <div className="flex gap-1">
                    {[30, 60, 90].map((days) => (
                        <Button
                            key={days}
                            variant={range === days ? "default" : "ghost"}
                            size="sm"
                            onClick={() => handleRangeChange(days)}
                            className={cn(
                                "text-xs h-7 px-2",
                                range === days && "btn-glow"
                            )}
                            data-testid={`range-${days}d`}
                        >
                            {days}D
                        </Button>
                    ))}
                </div>
            </CardHeader>
            <CardContent>
                <div className="h-[300px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                            <CartesianGrid 
                                strokeDasharray="3 3" 
                                stroke="hsl(210, 20%, 18%)" 
                                vertical={false}
                            />
                            <XAxis 
                                dataKey="date" 
                                tick={{ fontSize: 11, fill: 'hsl(220, 10%, 55%)' }}
                                tickLine={false}
                                axisLine={{ stroke: 'hsl(210, 20%, 18%)' }}
                                tickFormatter={(value) => {
                                    const date = new Date(value);
                                    return `${date.getMonth() + 1}/${date.getDate()}`;
                                }}
                            />
                            <YAxis 
                                tick={{ fontSize: 11, fill: 'hsl(220, 10%, 55%)' }}
                                tickLine={false}
                                axisLine={false}
                                tickFormatter={(value) => {
                                    if (unit === '$') return `$${value >= 1000 ? `${(value/1000).toFixed(0)}k` : value}`;
                                    if (unit === '%') return `${value}%`;
                                    return value >= 1000 ? `${(value/1000).toFixed(0)}k` : value;
                                }}
                            />
                            <Tooltip content={<CustomTooltip unit={unit} />} />
                            <Line 
                                type="monotone" 
                                dataKey="value" 
                                stroke="hsl(174, 95%, 69%)"
                                strokeWidth={2}
                                dot={false}
                                activeDot={{ r: 6, fill: 'hsl(174, 95%, 69%)', stroke: 'hsl(220, 15%, 4%)' }}
                            />
                            {anomalyPoints.map((point, idx) => (
                                <ReferenceDot
                                    key={idx}
                                    x={point.date}
                                    y={point.value}
                                    r={6}
                                    fill="hsl(348, 100%, 50%)"
                                    stroke="hsl(220, 15%, 4%)"
                                    strokeWidth={2}
                                />
                            ))}
                        </LineChart>
                    </ResponsiveContainer>
                </div>
                {anomalyPoints.length > 0 && (
                    <div className="flex items-center gap-4 mt-4 text-xs text-muted-foreground">
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-primary" />
                            <span>Normal</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-destructive" />
                            <span>Anomaly ({anomalyPoints.length})</span>
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

export default TrendChart;
